import React, { useEffect, useMemo, useState } from 'react';
import { Search, Trash2, Package, AlertTriangle } from 'lucide-react';
import API_URL from '@/config/api';
import { Input } from '@/components/ui/input';

let cache = null;
async function loadProducts() {
  if (cache) return cache;
  const res = await fetch(`${API_URL}/api/stock/products`, { credentials: 'include' });
  cache = res.ok ? await res.json() : [];
  return cache;
}
export const clearStockCache = () => { cache = null; };

const money = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Line cost inc VAT for the items that have a unit cost; missing = names without one. */
export const stockLineCost = (items, vatRate = 20) => {
  const div = 1 + Number(vatRate) / 100;
  let total = 0; const missing = [];
  for (const i of items) {
    if (i.unit_cost == null) missing.push(i.name);
    else total += Number(i.unit_cost) * Number(i.quantity || 0) * div;
  }
  return { total: Math.round(total * 100) / 100, missing, complete: items.length > 0 && missing.length === 0 };
};

/** Pick which warehouse products were taken out of stock for a 'Stock' cost line. */
export function StockPicker({ items, onChange, vatRate = 20, testId = 'stock-picker' }) {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState('');
  useEffect(() => { loadProducts().then(setProducts); }, []);

  useEffect(() => {
    if (!products.length) return;
    const needs = items.filter((i) => i.unit_cost === undefined);
    if (!needs.length) return;
    onChange(items.map((i) => { if (i.unit_cost !== undefined) return i; const p = products.find((x) => x.id === i.product_id); return { ...i, unit_cost: p?.unit_cost ?? null, cost_source: p?.cost_source ?? null, on_hand: i.on_hand ?? p?.qty_on_hand }; }));
  }, [products, items, onChange]);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return products.filter((p) => !items.some((i) => i.product_id === p.id) && (p.name.toLowerCase().includes(t) || (p.category || '').toLowerCase().includes(t))).slice(0, 8);
  }, [q, products, items]);

  const div = 1 + Number(vatRate) / 100;
  const add = (p) => { onChange([...items, { product_id: p.id, name: p.name, category: p.category, quantity: 1, on_hand: p.qty_on_hand, unit_cost: p.unit_cost ?? null, cost_source: p.cost_source ?? null }]); setQ(''); };
  const setQty = (id, v) => onChange(items.map((i) => (i.product_id === id ? { ...i, quantity: v } : i)));
  const remove = (id) => onChange(items.filter((i) => i.product_id !== id));
  const { total, missing } = stockLineCost(items, vatRate);

  return (
    <div className="ml-6 mt-1 mb-2 rounded-lg border border-emerald-200 bg-emerald-50/60 p-2.5 space-y-2" data-testid={testId}>
      <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-900 flex items-center gap-1"><Package className="w-3.5 h-3.5" /> What was taken out of stock?</div>
      {items.length > 0 && (
        <ul className="space-y-1" data-testid={`${testId}-items`}>
          {items.map((i) => (
            <li key={i.product_id} className="flex items-center gap-2 bg-white rounded-md border px-2 py-1" data-testid={`${testId}-item-${i.product_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{i.name}</div>
                <div className="text-[10px] text-gray-500 truncate flex items-center gap-1.5">
                  {i.unit_cost != null
                    ? <span data-testid={`${testId}-cost-${i.product_id}`}>{money(i.unit_cost)} ex VAT each · {i.cost_source === 'manual' ? 'portal cost' : 'Xero cost'}</span>
                    : <span className="inline-flex items-center gap-1 text-amber-700 font-semibold" data-testid={`${testId}-nocost-${i.product_id}`}><AlertTriangle className="w-3 h-3" /> no cost set</span>}
                  {i.on_hand != null && <span>· {i.on_hand} on hand</span>}
                </div>
              </div>
              <Input type="number" min="1" step="1" value={i.quantity} onChange={(e) => setQty(i.product_id, e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1))} className="h-8 w-20 bg-white text-center" data-testid={`${testId}-qty-${i.product_id}`} />
              <div className="w-20 text-right text-xs font-bold text-gray-800 tabular-nums" data-testid={`${testId}-line-${i.product_id}`}>{i.unit_cost != null ? money(i.unit_cost * Number(i.quantity || 0) * div) : '—'}</div>
              <button type="button" onClick={() => remove(i.product_id)} className="p-1 text-gray-400 hover:text-rose-600" aria-label="Remove product" data-testid={`${testId}-remove-${i.product_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={products.length ? 'Search products…' : 'Loading products…'} className="h-8 pl-7 bg-white text-sm" data-testid={`${testId}-search`} />
        {matches.length > 0 && (
          <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid={`${testId}-results`}>
            {matches.map((p) => (
              <li key={p.id}>
                <button type="button" onClick={() => add(p)} className="w-full text-left px-3 py-1.5 hover:bg-emerald-50 flex items-center justify-between gap-2" data-testid={`${testId}-result-${p.id}`}>
                  <span className="min-w-0"><span className="text-sm font-semibold block truncate">{p.name}</span><span className="text-[10px] text-gray-500">{p.category}{p.unit_cost != null ? ` · cost ${money(p.unit_cost)} ex VAT` : ' · no cost set'}</span></span>
                  <span className={`text-xs font-bold shrink-0 ${p.qty_on_hand > 0 ? 'text-emerald-700' : 'text-rose-600'}`}>{p.qty_on_hand} on hand</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-[11px] text-emerald-900/70">Search and add each product — the cost is worked out from Xero and the quantity is knocked off the warehouse when you save.</p>
      ) : missing.length > 0 ? (
        <p className="text-[11px] text-amber-800 flex items-start gap-1" data-testid={`${testId}-missing-warning`}><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> No cost for {missing.join(', ')} — type the £ for this line yourself, or set a cost on the Pricing page.</p>
      ) : (
        <p className="text-[11px] text-emerald-900 font-semibold" data-testid={`${testId}-total`}>Calculated cost {money(total)} inc VAT</p>
      )}
    </div>
  );
}
